/**
 * Team Match Badges
 * Adds badges to product cards showing when a team will play next
 */

(function() {
  'use strict';

  // Load match data
  function loadMatchData() {
    const scriptTag = document.getElementById('team-matches-data');
    if (!scriptTag) return null;
    
    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.error('Error parsing team matches data:', e);
      return null;
    }
  }

  // Normalize team name for comparison (same as tag handle format)
  function normalizeTeamName(name) {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }

  // Find the earliest upcoming match for a team
  function findEarliestMatchForTeam(teamName, matches) {
    const normalizedTeam = normalizeTeamName(teamName);
    const now = Math.floor(Date.now() / 1000);
    
    let earliestMatch = null;
    let earliestTimestamp = Infinity;
    
    matches.forEach(match => {
      const timestamp = parseInt(match.timestamp) || 0;
      if (timestamp <= now) return; // Skip past matches
      
      const homeNormalized = normalizeTeamName(match.home);
      const awayNormalized = normalizeTeamName(match.away);
      
      if (homeNormalized === normalizedTeam || awayNormalized === normalizedTeam) {
        if (timestamp < earliestTimestamp) {
          earliestTimestamp = timestamp;
          earliestMatch = match;
        }
      }
    });
    
    return earliestMatch;
  }

  // Calculate days until match
  function calculateDaysUntilMatch(timestamp) {
    const now = Date.now();
    const matchDate = new Date(timestamp * 1000);
    const diffTime = matchDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  // Format badge text in Arabic
  function formatBadgeText(days) {
    if (days === 0) {
      return 'يلعب اليوم';
    } else if (days === 1) {
      return 'يلعب غدا';
    } else if (days === 2) {
      return 'يلعب خلال يومين';
    } else {
      return `يلعب خلال ${days} أيام`;
    }
  }

  // Check if product tags match any team - returns the EARLIEST match across all tags
  function findMatchingTeam(productTags, matches) {
    if (!productTags) return null;
    
    const tagsArray = productTags.split(',').map(tag => tag.trim().toLowerCase());
    
    let earliestMatch = null;
    let earliestTimestamp = Infinity;
    
    // Check all tags and find the earliest match across all of them
    for (const tag of tagsArray) {
      const match = findEarliestMatchForTeam(tag, matches);
      if (match) {
        const timestamp = parseInt(match.timestamp) || 0;
        if (timestamp < earliestTimestamp) {
          earliestTimestamp = timestamp;
          earliestMatch = {
            teamName: normalizeTeamName(tag) === normalizeTeamName(match.home) ? match.home : match.away,
            timestamp: match.timestamp
          };
        }
      }
    }
    
    return earliestMatch;
  }

  // Create badge element
  function createBadge(text) {
    const badge = document.createElement('div');
    badge.className = 'product-badges__badge product-badges__badge--rectangle product-badges__badge--team-match';
    badge.textContent = text;
    return badge;
  }

  // Add badge to product card
  function addBadgeToProductCard(productCard, badgeText) {
    // Check if badge already exists anywhere in the product card to avoid duplicates
    const existingBadge = productCard.querySelector('.product-badges__badge--team-match');
    if (existingBadge) {
      // Update text if it changed, but don't create duplicate
      if (existingBadge.textContent !== badgeText) {
        existingBadge.textContent = badgeText;
      }
      return;
    }
    
    // Also check if this card has already been processed (using data attribute)
    if (productCard.dataset.teamBadgeProcessed === 'true') {
      return;
    }
    
    // Mark as processed
    productCard.dataset.teamBadgeProcessed = 'true';
    
    // Find or create badges container
    let badgesContainer = productCard.querySelector('.product-badges');
    
    if (!badgesContainer) {
      // Create badges container if it doesn't exist
      badgesContainer = document.createElement('div');
      badgesContainer.className = 'product-badges product-badges--top-left';
      
      // Find the card gallery or image container to position badge
      const gallery = productCard.querySelector('.card-gallery') || 
                      productCard.querySelector('[class*="gallery"]') ||
                      productCard.querySelector('.product-card__content') ||
                      productCard;
      
      if (gallery) {
        const computedStyle = window.getComputedStyle(gallery);
        if (computedStyle.position === 'static') {
          gallery.style.position = 'relative';
        }
        gallery.appendChild(badgesContainer);
      }
    }
    
    // Add the badge
    const badge = createBadge(badgeText);
    badgesContainer.appendChild(badge);
  }

  // Process all product cards on the page
  function processProductCards() {
    const matchData = loadMatchData();
    if (!matchData || !matchData.matches || matchData.matches.length === 0) {
      return;
    }

    // Find all product items with tags (collection pages)
    const productItems = document.querySelectorAll('[data-product-tags]');
    
    productItems.forEach(item => {
      const productTags = item.dataset.productTags;
      if (!productTags) return;
      
      // Skip if already processed
      if (item.dataset.teamBadgeProcessed === 'true') {
        return;
      }
      
      // Find matching team (earliest match across all tags)
      const matchInfo = findMatchingTeam(productTags, matchData.matches);
      if (matchInfo) {
        const days = calculateDaysUntilMatch(matchInfo.timestamp);
        const badgeText = formatBadgeText(days);
        
        // Find the product card within this item
        const productCard = item.querySelector('.product-card, product-card, [class*="product-card"]');
        if (productCard) {
          addBadgeToProductCard(productCard, badgeText);
          // Mark item as processed
          item.dataset.teamBadgeProcessed = 'true';
        } else {
          // Fallback: add to the item itself
          addBadgeToProductCard(item, badgeText);
          item.dataset.teamBadgeProcessed = 'true';
        }
      } else {
        // Mark as processed even if no match (to avoid re-checking)
        item.dataset.teamBadgeProcessed = 'true';
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processProductCards);
  } else {
    processProductCards();
  }

  // Also process when new products are added (for infinite scroll, etc.)
  // Use debouncing to avoid processing the same cards multiple times
  let processingTimeout = null;
  const observer = new MutationObserver(() => {
    // Clear any pending processing
    if (processingTimeout) {
      clearTimeout(processingTimeout);
    }
    // Debounce: wait 100ms after mutations stop before processing
    processingTimeout = setTimeout(() => {
      processProductCards();
    }, 100);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();

